import {
  TemplateFragmentUsageModel,
  TemplateFragmentUsageResultModel,
} from '@/typings/api/emailCenter';

/**
 * What to do about a delete, given what the usage pre-flight found.
 *
 * The one thing worth being careful about here: **in use is not the same as
 * undeletable**. Where a partner row shares its key with a standard one, the
 * global row keeps answering for the key once the partner's is gone, so every
 * `{% include %}` still resolves and the emails simply look standard again.
 * That delete stays available however widely the key is used, and the
 * confirmation says what it really does rather than warning about nothing.
 *
 * New blocks never share a key (copying a standard block produces a key of its
 * own), so this now only applies to rows created while overriding was offered —
 * which is exactly why the check is on `has_fallback` from the server rather
 * than on anything the client infers from ownership.
 *
 * A partner-invented key has nothing behind it. Deleting it leaves every
 * reference pointing at nothing, and the server's fragment loader throws while
 * *sending* — so that one is refused, with the references named
 * (`App\Support\Email\FragmentUsage` applies the same rule and answers 409).
 */
export interface FragmentDeletionDecision {
  /** Refuse: references would be left dangling, with nothing to fall back on. */
  blocked: boolean;
  /** The delete restores the standard version instead of removing the block. */
  restoresStandard: boolean;
  /**
   * The pre-flight could not be read, so this is a guess.
   *
   * Offered rather than refused: the server runs the same check on the DELETE
   * itself, so the worst case is a 409 explaining why — whereas refusing on a
   * failed lookup would make a broken endpoint look like an undeletable block.
   */
  unverified: boolean;
  /** What references it — worth showing even when the delete is allowed. */
  usages: TemplateFragmentUsageModel[];
}

export function decideFragmentDeletion(
  usage: TemplateFragmentUsageResultModel | null | undefined,
): FragmentDeletionDecision {
  if (!usage) {
    return {
      blocked: false,
      restoresStandard: false,
      unverified: true,
      usages: [],
    };
  }

  return {
    blocked: usage.blocks_deletion,
    restoresStandard: usage.has_fallback,
    unverified: false,
    usages: usage.usages ?? [],
  };
}
